import { z } from 'zod';
import { defineManifest } from '@dashboard/module-contract/manifest';
import type { RolesOf } from '@dashboard/module-contract/manifest';

/**
 * Route 53 zones and records — the edits no other module makes.
 *
 * Every other module publishes its own records: Email writes its DKIM CNAMEs
 * and MAIL FROM records, Sites writes its alias and validation records.
 * What is left for this module is what **nothing else creates** — a Google
 * Workspace MX, a verification TXT, DMARC, an apex record for a site hosted
 * elsewhere. Real needs, and not universal ones, so this is a flexibility
 * addition for people who understand DNS rather than a default.
 */

export const settings = z.object({
	/**
	 * Which zones this module may read, by id. Empty means every zone in the
	 * account except the deployment's own.
	 *
	 * An in-app setting rather than a deploy argument (`D19`): a client may not
	 * want the dashboard looking at every zone they hold, and that list should
	 * change without a redeploy.
	 */
	zones: z.array(z.string()).default([]),
});

export type DnsSettings = z.infer<typeof settings>;

export const manifest = defineManifest({
	id: 'dns',
	title: 'DNS',
	/**
	 * The full ladder, now that every rung confers something real.
	 *
	 * `operator` edits records (`D128`); `admin` creates and adopts zones and
	 * chooses which zones this module may read (`D109`, `D122`). Both are
	 * decisions about **resources**, which is why they are module roles rather
	 * than `core:admin`.
	 *
	 * The question the module spec flagged as unresolved — whether
	 * `route53:ChangeResourceRecordSets` can be scoped, or whether v1 grants it
	 * account-wide and leans on an application refusal — is answered in
	 * `infra.ts`: Route 53 publishes condition keys, so the record types are
	 * real IAM and the zone and the record's owner are application refusals.
	 */
	roles: [
		{ name: 'reader', title: 'Reader', description: 'View zones and records' },
		{
			/**
			 * The ladder's middle rung, declared now that it confers something
			 * (`D128`). It was described in the spec and deliberately left
			 * undeclared while record edits did not exist — a grant that
			 * confers nothing reads as access somebody has.
			 */
			name: 'operator',
			title: 'Operator',
			description: 'Add, change and remove records in zones this dashboard manages',
			includes: ['reader'],
		},
		{
			name: 'admin',
			title: 'Administrator',
			description: 'Everything above, plus creating and adopting zones',
			includes: ['operator'],
		},
	],
	nav: [
		{ path: '', title: 'Zones', requiredRole: 'dns:reader' },
		{ path: 'settings', title: 'Settings', requiredRole: 'dns:admin' },
	],
	settings,
});

export type DnsRole = RolesOf<typeof manifest>;
